import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  Camera,
  CarFront,
  ChevronRight,
  FileText,
  Globe2,
  Mail,
  MessageCircle,
  Phone,
  Search,
  Upload,
  X,
} from 'lucide-react';

import { AuthGuard } from '../../app/AuthGuard';
import { RoleSettingsCard } from '../../ui/RoleSettingsCard/RoleSettingsCard';
import { documentPreview } from '../../features/carrierVerification/documentPreview';
import { openPhotoGallery } from '../../utils/photoGallery';
import type { ScreenId } from '../../app/screenRoutes';
import {
  carrierCabinetApi,
  type BillingSummaryDto,
  type CarrierProfileDto,
  type TransportDto,
  type TransportRequestDto,
} from '../../features/carrierCabinet/api';
import {
  attachCarrierDocument,
  getCarrierDocuments,
  getCarrierProfile,
  submitCarrierVerification,
  uploadAsset,
  type CarrierDocument,
  type DocumentType,
  type CarrierProfile as VerificationProfile,
} from '../../features/carrierVerification/api';
import { CarrierCabinetLayout, navigateCarrier } from '../../layouts/CarrierCabinet/CarrierCabinetLayout';
import { useAuth } from '../../state/auth';
import { useLanguage } from '../../i18n/LanguageProvider';
import { CabinetLoadState } from '../../ui/CabinetEmptyState/CabinetEmptyState';
import {
  AccountActions,
  LocaleSettingsCard,
  NotificationOptionsCard,
  ProfileLink,
  SecurityCard,
  Stars,
  SupportHelpView,
  Tile,
  type StarsRating,
} from '../../ui';
import { hashNumber, money, PageHeading } from './carrierCabinetShared';
import './carrier-support.css';

export type CarrierSupportScreenId = Extract<
  ScreenId,
  'carrier-review' | 'carrier-dispute' | 'carrier-help' | 'carrier-documents' | 'carrier-settings'
>;

const FIGMA_NODES: Record<CarrierSupportScreenId, { '1920': string; '1440': string }> = {
  'carrier-review': { '1920': '2607:35994', '1440': '2607:36394' },
  'carrier-dispute': { '1920': '2661:35245', '1440': '2661:35508' },
  'carrier-help': { '1920': '2719:36145', '1440': '2719:36809' },
  'carrier-documents': { '1920': '2719:37544', '1440': '2720:37835' },
  'carrier-settings': { '1920': '2680:40911', '1440': '2680:40911' },
};

type ViewState = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

function RatingPicker({ compact = false, disabled = false, label, onChange, value }: {
  compact?: boolean;
  disabled?: boolean;
  label: string;
  onChange: (rating: number) => void;
  value: number;
}) {
  return (
    <div className={`carrier-rating-picker ${compact ? 'is-compact' : ''}`}>
      <span>{label}</span>
      <div className="carrier-rating-picker__control">
        <Stars mode="Light" rating={value as StarsRating} />
        <div className="carrier-rating-picker__hits">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              aria-label={`${label}: ${rating} из 5`}
              aria-pressed={rating === value}
              key={rating}
              disabled={disabled}
              type="button"
              onClick={() => onChange(rating)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewView() {
  const { language } = useLanguage();
  const requestedTransportId = hashNumber('transport');
  const [transport, setTransport] = useState<TransportDto | null>(null);
  const [request, setRequest] = useState<TransportRequestDto | null>(null);
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [overall, setOverall] = useState(5);
  const [comment, setComment] = useState('');
  const [submitError, setSubmitError] = useState(false);
  const submitting = useRef(false);
  const loadVersion = useRef(0);
  const [state, setState] = useState<ViewState>('loading');
  const canReview = Boolean(transport && (transport.status === 'closed' || (transport.status === 'delivered' && transport.customer_received_at)));

  async function load() {
    const version = ++loadVersion.current;
    const [nextProfile, transports, reviews] = await Promise.all([
      carrierCabinetApi.profile(),
      carrierCabinetApi.transports(),
      carrierCabinetApi.reviews(),
    ]);
    const nextTransport = requestedTransportId
      ? transports.find(item => item.id === requestedTransportId) ?? null
      : transports.find(item => item.status === 'closed' || (item.status === 'delivered' && item.customer_received_at)) ?? null;
    const nextRequest = nextTransport ? await carrierCabinetApi.request(nextTransport.request_id) : null;
    if (version !== loadVersion.current) return;
    setProfile(nextProfile);
    setTransport(nextTransport);
    setRequest(nextRequest);
    const ownReview = reviews.find(item => item.transport === nextTransport?.id && item.author_role === 'carrier');
    setAlreadyReviewed(Boolean(ownReview));
    if (ownReview) { setOverall(ownReview.rating_overall); setComment(ownReview.comment); }
    setState('ready');
  }

  useEffect(() => {
    setState('loading');
    void load().catch(() => setState('error'));
    return () => { loadVersion.current += 1; };
  }, [requestedTransportId]);

  async function submit() {
    if (!transport || !canReview || alreadyReviewed || state !== 'ready' || submitting.current) return;
    submitting.current = true;
    setSubmitError(false);
    setState('submitting');
    try {
      await carrierCabinetApi.createReview({
        transport: transport.id,
        author_role: 'carrier',
        rating_overall: overall,
        comment: comment.trim(),
      });
      setAlreadyReviewed(true);
      setState('success');
    } catch {
      // Recover a successful submission if its response was lost, without sending twice.
      try {
        const reviews = await carrierCabinetApi.reviews();
        setAlreadyReviewed(reviews.some(item => item.transport === transport.id && item.author_role === 'carrier'));
      } catch { /* Preserve the draft when the server is unavailable. */ }
      setSubmitError(true);
      setState('ready');
    } finally {
      submitting.current = false;
    }
  }

  if (state === 'loading' || state === 'error') return <CabinetLoadState state={state} />;
  if (!transport || !request || !profile) return <p>{language === 'en' ? 'Transport not found.' : 'Перевозка не найдена.'}</p>;

  return (
    <div className="carrier-review" data-state={state}>
      <PageHeading
        className="carrier-review__top"
        figmaNode="2607:36583"
        title="Расскажите как прошло"
        subtitle={<>
          <span data-i18n-skip>{language === 'en' ? `Transport #${transport.request_code} is completed` : `Перевозка #${transport.request_code} завершена`}</span>
          <span data-i18n-skip>{language === 'en'
            ? `You earned ${money(transport.net_price_minor, transport.currency)} after fees`
            : `Вы заработали ${money(transport.net_price_minor, transport.currency)} после вычета комиссий`}</span>
        </>}
      />

      <div className="carrier-review__alert" data-figma-node="2607:36593">
        <span data-i18n-skip>{alreadyReviewed
          ? (language === 'en' ? 'Your review has been submitted. Only approved reviews affect the rating.' : 'Ваш отзыв отправлен. В рейтинге учитываются только одобренные отзывы.')
          : canReview
            ? (language === 'en' ? 'Your review will be checked by a moderator before publication.' : 'Ваш отзыв будет проверен модератором перед публикацией.')
            : (language === 'en' ? 'Reviews are available after the customer confirms receiving the pet.' : 'Отзыв доступен после подтверждения получения питомца заказчиком.')}</span>
      </div>

      <section className="carrier-review__form" data-figma-node="2607:36600">
        <div className="carrier-review__customer">
          <span>Ваша оценка заказчика</span>
          <div>
            <b aria-hidden="true">{transport.customer_name.slice(0, 1).toUpperCase()}</b>
            <p><strong><ProfileLink profileId={transport.customer_id} role="customer">{transport.customer_name}</ProfileLink></strong><small>{request.customer_rating} ★ <i /> {request.customer_completed_requests_count} перевозок</small></p>
          </div>
        </div>
        <div className="carrier-review__ratings">
          <RatingPicker label="Общая оценка" disabled={alreadyReviewed || !canReview || state !== 'ready'} value={overall} onChange={setOverall} />
        </div>
        <label className="carrier-review__comment">
          <span>Комментарий</span>
          <textarea rows={4} disabled={alreadyReviewed || !canReview || state !== 'ready'} value={comment} placeholder="Что понравилось, что можно улучшить" onChange={(event) => setComment(event.target.value)} />
        </label>
      </section>

      <div className="carrier-review__actions" data-figma-node="2607:36755">
        {submitError && !alreadyReviewed ? <p role="alert" data-i18n-skip>{language === 'en' ? 'Could not send the review. Please try again.' : 'Не удалось отправить отзыв. Попробуйте ещё раз.'}</p> : null}
        <button className="carrier-outline-button" type="button" onClick={() => navigateCarrier('carrier-completed')}>Оставить отзыв позже</button>
        <button className="carrier-green-button" disabled={alreadyReviewed || !canReview || state !== 'ready'} type="button" onClick={() => void submit()} data-i18n-skip>
          {alreadyReviewed ? (language === 'en' ? 'Review submitted' : 'Отзыв оставлен') : (language === 'en' ? 'Submit review' : 'Отправить отзыв')}
        </button>
      </div>
    </div>
  );
}

const DISPUTE_REASONS = [
  ['pet_condition', 'Состояние питомца', 'Есть вопросы к состоянию питомца при передаче'],
  ['handover', 'Передача питомца', 'Заказчик не готов к передаче или не выходит на связь'],
  ['delivery_delay', 'Нарушены сроки', 'Сроки изменены без предварительного согласования'],
  ['conditions', 'Нарушены условия', 'Фактические условия отличаются от заявки'],
  ['payment', 'Вопрос по оплате', 'Оплата не поступила или сумма не совпадает'],
  ['other', 'Другое', 'Опишите ситуацию свободно'],
] as const;

function DisputeView() {
  const requestedDealId = hashNumber('deal');
  const requestedTransportId = hashNumber('transport');
  const evidenceInput = useRef<HTMLInputElement>(null);
  const [transport, setTransport] = useState<TransportDto | null>(null);
  const [request, setRequest] = useState<TransportRequestDto | null>(null);
  const [chatId, setChatId] = useState<number | null>(null);
  const [reason, setReason] = useState<(typeof DISPUTE_REASONS)[number][0]>('pet_condition');
  const [description, setDescription] = useState('');
  const [evidence, setEvidence] = useState<Array<{ id: number; name: string }>>([]);
  const [state, setState] = useState<ViewState>('loading');

  async function load() {
    const [transports, chats] = await Promise.all([carrierCabinetApi.transports(), carrierCabinetApi.chats()]);
    const nextTransport = transports.find((item) => item.deal === requestedDealId)
      ?? transports.find((item) => item.id === requestedTransportId)
      ?? transports.find((item) => ['planned', 'in_progress', 'delivered'].includes(item.status))
      ?? transports[0]
      ?? null;
    const nextRequest = nextTransport ? await carrierCabinetApi.request(nextTransport.request_id) : null;
    setTransport(nextTransport);
    setRequest(nextRequest);
    setChatId(chats.find((item) => item.deal === nextTransport?.deal)?.id ?? null);
    setState('ready');
  }

  useEffect(() => {
    void load().catch(() => setState('error'));
  }, [requestedDealId, requestedTransportId]);

  async function addEvidence(file?: File) {
    if (!file) return;
    setState('submitting');
    try {
      const asset = await carrierCabinetApi.uploadDisputeEvidence(file);
      setEvidence((items) => [...items, { id: asset.id, name: asset.original_filename || file.name }]);
      setState('ready');
    } catch {
      setState('error');
    } finally {
      if (evidenceInput.current) evidenceInput.current.value = '';
    }
  }

  async function submit() {
    if (!transport || !description.trim()) return;
    setState('submitting');
    try {
      await carrierCabinetApi.createDispute({
        deal: transport.deal,
        opened_by_role: 'carrier',
        reason,
        description: description.trim(),
        expected_resolution: 'Разобраться в ситуации',
        evidence: evidence.map((item) => item.id),
      });
      setState('success');
      if (chatId) navigateCarrier('carrier-chat', { chat: chatId });
      else navigateCarrier('carrier-active-transport', { id: transport.id });
    } catch {
      setState('error');
    }
  }

  if (!transport || !request) return <div className="carrier-dispute" data-state={state} />;

  const goBack = () => chatId
    ? navigateCarrier('carrier-chat', { chat: chatId })
    : navigateCarrier('carrier-active-transport', { id: transport.id });

  return (
    <div className="carrier-dispute" data-state={state}>
      <button className="carrier-dispute__back" type="button" onClick={goBack}>← К чату</button>
      <PageHeading
        className="carrier-dispute__top"
        figmaNode="2661:35732"
        title="Что-то пошло не так?"
        subtitle={<>Заявка #{transport.request_code} <i /> {transport.from_city} → {transport.to_city} <i /> заказчик <ProfileLink profileId={transport.customer_id} role="customer">{transport.customer_name}</ProfileLink></>}
      />
      <div className="carrier-dispute__content" data-figma-node="2661:35748">
        <div className="carrier-dispute__alert">Подавайте спор только если действительно есть проблема. Модератор LOVO разберёт обращение в течение 24 часов.</div>
        <section className="carrier-dispute__card">
          <fieldset className="carrier-dispute__reasons">
            <legend>Причина спора</legend>
            <div>
              {DISPUTE_REASONS.map(([value, title, note]) => (
                <Tile active={reason === value} description={note} key={value} title={title} onClick={() => setReason(value)} />
              ))}
            </div>
          </fieldset>
          <label className="carrier-dispute__description"><span>Что произошло?</span><textarea value={description} placeholder="Опишите ситуацию подробно" onChange={(event) => setDescription(event.target.value)} /></label>
          <label className="carrier-dispute__evidence"><span>Приложите доказательства</span><button type="button" onClick={() => evidenceInput.current?.click()}><Upload aria-hidden="true" size={18} /> {evidence.length ? `Загружено файлов: ${evidence.length}` : 'Загрузить файлы'}</button></label>
          <input ref={evidenceInput} hidden type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(event) => void addEvidence(event.target.files?.[0])} />
          <div className="carrier-dispute__privacy">Документы и переписка доступны только участникам сделки и команде LOVO.</div>
        </section>
        <div className="carrier-dispute__actions">
          <button className="carrier-outline-button" type="button" onClick={goBack}>Отменить</button>
          <button className="carrier-green-button" disabled={!description.trim() || state === 'submitting'} type="button" onClick={() => void submit()}>Отправить</button>
        </div>
      </div>
    </div>
  );
}

const FAQ = [
  ['Когда я получу оплату за перевозку?', 'Оплата поступает на внутренний баланс LOVO сразу после подтверждения заказчиком получения питомца, обычно в течение нескольких минут.'],
  ['Как рассчитывается комиссия LOVO?', 'Комиссия составляет 5% от суммы перевозки и автоматически формируется в виде счёта после завершения заказа.'],
  ['Что делать, если заказчик отменил заявку?', 'Если заявка отменена до выезда — комиссия не взимается. Если после — обратитесь в поддержку для разбора ситуации.'],
  ['Как повысить рейтинг фото?', 'Добавляйте фото на каждой контрольной точке перевозки — чем полнее хроника поездки, тем выше рейтинг.'],
  ['Что делать при споре с заказчиком?', 'Опишите ситуацию в чате поддержки — мы рассмотрим детали заявки, переписку и фото с контрольных точек.'],
] as const;

function HelpView() {
  const [billing, setBilling] = useState<BillingSummaryDto | null>(null);

  useEffect(() => {
    void carrierCabinetApi.billing().then(setBilling).catch(() => {});
  }, []);

  return (
    <SupportHelpView
      role="carrier"
      supportEmail={billing?.support_email || 'help@lovo.ru'}
      supportPhone={billing?.support_phone || '8 800 555-35-35'}
    />
  );
}

interface DocumentDefinition {
  key: string;
  label: string;
  type: DocumentType;
  icon: typeof FileText;
}

const DOCUMENTS: DocumentDefinition[] = [
  { key: 'identity', label: 'Паспорт', type: 'identity', icon: FileText },
  { key: 'selfie', label: 'Селфи с паспортом', type: 'selfie', icon: Camera },
  { key: 'driver', label: 'Водительские права', type: 'driver_license', icon: CarFront },
  { key: 'vehicle-registration', label: 'СТС / регистрация автомобиля', type: 'vehicle_registration', icon: CarFront },
  { key: 'company', label: 'Документы компании', type: 'company', icon: FileText },
  { key: 'license-local', label: 'Лицензия ИП', type: 'license', icon: FileText },
  { key: 'license-international', label: 'Международная лицензия', type: 'license', icon: Globe2 },
];

function findDocument(documents: CarrierDocument[], key: string, type: DocumentType): CarrierDocument | undefined {
  if (type === 'license') {
    const licenses = documents.filter((d) => d.document_type === 'license');
    if (key === 'license-international') {
      const international = licenses.find((d) => (d.title || '').toLowerCase().includes('международ'));
      return international || (licenses.length > 1 ? licenses[1] : undefined);
    }
    const local = licenses.find((d) => !(d.title || '').toLowerCase().includes('международ'));
    return local || licenses[0];
  }
  return documents.find((d) => d.document_type === type);
}

function documentStatus(document?: CarrierDocument) {
  if (!document) return 'Не загружен';
  if (document.status === 'approved') return 'Подтверждён ✓';
  if (document.status === 'rejected') return 'Отклонён';
  return 'На проверке';
}

function DocumentPreview({ document, title }: { document: CarrierDocument; title: string }) {
  const preview = documentPreview(document);
  const [failedSources, setFailedSources] = useState(0);
  useEffect(() => setFailedSources(0), [preview.url]);
  const showImage = preview.isImage && failedSources < 2;

  return (
    <a
      className="carrier-documents__preview"
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Просмотреть документ: ${title}`}
      title={`Просмотреть документ: ${title}`}
      onClick={(event) => {
        if (showImage) {
          event.preventDefault();
          openPhotoGallery([{ url: preview.url, alt: title }], 0);
        }
      }}
    >
      <FileText aria-hidden="true" size={18} />
      {showImage ? (
        <img
          src={failedSources === 0 ? preview.thumbnail : preview.url}
          alt=""
          onError={() => setFailedSources((current) => current + 1)}
        />
      ) : null}
    </a>
  );
}

function DocumentsView() {
  const input = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<CarrierDocument[]>([]);
  const [profile, setProfile] = useState<VerificationProfile | null>(null);
  const [target, setTarget] = useState<DocumentDefinition>(DOCUMENTS[0]);
  const [state, setState] = useState<ViewState>('loading');
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  async function load() {
    try {
      const [data, nextProfile] = await Promise.all([getCarrierDocuments(), getCarrierProfile()]);
      setDocuments(data);
      setProfile(nextProfile);
      setState('ready');
    } catch (err) {
      setState('error');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function choose(definition: DocumentDefinition) {
    setSaved(false);
    setSaveError('');
    setTarget(definition);
    input.current?.click();
  }

  async function handleSave() {
    setState('submitting');
    setSaved(false);
    setSaveError('');
    try {
      await submitCarrierVerification();
      await load();
      setSaved(true);
    } catch (err) {
      setState('ready');
      setSaveError(err instanceof Error ? err.message : 'Не удалось сохранить документы. Попробуйте ещё раз.');
    }
  }

  async function upload(file?: File) {
    if (!file) return;
    setState('submitting');
    try {
      const asset = await uploadAsset(file, 'document', 'private');
      const title = target.key === 'license-international' ? 'Международная лицензия' : target.label;
      await attachCarrierDocument(target.type, asset.id, title);
      await load();
    } catch (err) {
      setState('error');
    } finally {
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="carrier-documents" data-state={state}>
      <PageHeading
        className="carrier-documents__top"
        figmaNode="2720:38035"
        title="Документы"
        subtitle="Статус верификации ваших документов перевозчика"
      />
      <section className="carrier-documents__card carrier-documents__list" data-figma-node="2720:38044">
        <h2>Полезные материалы</h2>
        <div>
          {DOCUMENTS.filter((definition) => {
            if (definition.type === 'vehicle_registration') return profile?.service_type === 'auto';
            if (definition.type === 'company') return profile?.carrier_type === 'company';
            return true;
          }).map((definition) => {
            const document = findDocument(documents, definition.key, definition.type);
            const Icon = definition.icon;
            return (
              <article key={definition.key}>
                <span className="carrier-documents__item-title">
                  <Icon aria-hidden="true" size={20} />
                  <span>{definition.label}</span>
                </span>
                <div className="carrier-documents__item-actions">
                  {document ? <DocumentPreview document={document} title={definition.label} /> : null}
                  <strong className={`is-${document?.status ?? 'missing'}`}>
                    {documentStatus(document)}
                  </strong>
                  <button type="button" onClick={() => choose(definition)}>
                    {document ? 'Заменить' : 'Загрузить'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      <section className="carrier-documents__card carrier-documents__update" data-figma-node="2720:38040">
        <h2>Нужно обновить документы?</h2>
        <p>Если срок действия документа истёк или данные изменились — загрузите новую версию. Проверка обычно занимает до 24 часов.</p>
        <button
          className="carrier-outline-button"
          type="button"
          disabled={state === 'loading' || state === 'submitting' || documents.length === 0}
          onClick={() => void handleSave()}
        >
          {state === 'submitting' ? 'Сохраняем…' : saved ? 'Документы сохранены ✓' : 'Сохранить документы'}
        </button>
        {saveError ? <p className="carrier-documents__save-error" role="alert">{saveError}</p> : null}
      </section>
      <input ref={input} hidden type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(event) => void upload(event.target.files?.[0])} />
    </div>
  );
}

function SettingsView() {
  const auth = useAuth();
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);

  useEffect(() => {
    let active = true;
    carrierCabinetApi.profile().then((data) => {
      if (active) setProfile(data);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const toggle = async (field: 'push_enabled' | 'sms_active_transport' | 'email_marketing') => {
    if (!profile) return;
    const nextValue = !profile[field];
    setProfile({ ...profile, [field]: nextValue });
    try {
      await carrierCabinetApi.updateProfile({ [field]: nextValue });
    } catch {
      setProfile({ ...profile, [field]: !nextValue });
    }
  };

  const deleteAccount = async () => {
    if (window.confirm('Вы уверены, что хотите удалить аккаунт? Это действие необратимо.')) {
      await auth.deleteAccount();
      window.location.hash = 'landing';
    }
  };

  return (
    <div className="carrier-settings">
      <PageHeading
        className="carrier-settings__top"
        subtitle="Управление безопасностью, способами входа и уведомлениями"
        title="Настройки"
      />

      {/* Card 1: Безопасность */}
      <SecurityCard
        onChangePassword={async (currentPassword, newPassword) => {
          await carrierCabinetApi.changePassword(currentPassword, newPassword);
        }}
      />

      {/* Card 2: Уведомления */}
      <NotificationOptionsCard
        emailMarketing={profile?.email_marketing}
        pushEnabled={profile?.push_enabled}
        smsActiveTransport={profile?.sms_active_transport}
        onToggleEmailMarketing={() => void toggle('email_marketing')}
        onTogglePush={() => void toggle('push_enabled')}
        onToggleSms={() => void toggle('sms_active_transport')}
      />

      {/* Card 3: Язык и регион */}
      <LocaleSettingsCard
        timezone={profile?.timezone}
        displayCurrency={profile?.display_currency}
        onSaveLocale={async (patch) => {
          if (!profile) return;
          setProfile({ ...profile, ...patch });
          try {
            await carrierCabinetApi.updateProfile(patch);
          } catch {
            const fresh = await carrierCabinetApi.profile();
            setProfile(fresh);
          }
        }}
      />

      {/* Account Actions */}
      <RoleSettingsCard />
      <AccountActions
        onDeleteAccount={() => void deleteAccount()}
        onSignOut={() => void auth.signOut().then(() => { window.location.hash = 'landing'; })}
      />
    </div>
  );
}

export function CarrierSupportScreen({ screen }: { screen: CarrierSupportScreenId }) {
  return (
    <AuthGuard allow={['carrier']}>
      <CarrierCabinetLayout figmaNode={FIGMA_NODES[screen]} screen={screen}>
        {screen === 'carrier-review' ? <ReviewView /> : null}
        {screen === 'carrier-dispute' ? <DisputeView /> : null}
        {screen === 'carrier-help' ? <HelpView /> : null}
        {screen === 'carrier-documents' ? <DocumentsView /> : null}
        {screen === 'carrier-settings' ? <SettingsView /> : null}
      </CarrierCabinetLayout>
    </AuthGuard>
  );
}
